import React, { useState, useRef, useEffect } from "react";
import { HiChevronDown } from "react-icons/hi";

export function Select({ value, onValueChange, children }) {
  return (
    <SelectContext.Provider value={{ value, onValueChange }}>
      {children}
    </SelectContext.Provider>
  );
}

const SelectContext = React.createContext({
  value: "",
  onValueChange: () => {},
});

export function SelectTrigger({ children, className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  return (
    <SelectTriggerContext.Provider value={{ isOpen, setIsOpen, triggerRef }}>
      <button
        ref={triggerRef}
        type="button"
        className={`flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {children}
        <HiChevronDown size={16} className="ml-2 opacity-50" />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setIsOpen(false)}
        />
      )}
    </SelectTriggerContext.Provider>
  );
}

const SelectTriggerContext = React.createContext({
  isOpen: false,
  setIsOpen: () => {},
  triggerRef: null,
});

export function SelectValue({ placeholder }) {
  const context = React.useContext(SelectContext);
  return <span>{context.value || placeholder}</span>;
}

export function SelectContent({ children, className = "" }) {
  const { isOpen, triggerRef } = React.useContext(SelectTriggerContext);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={contentRef}
      className={`absolute z-50 min-w-[8rem] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg ${className}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
      }}
    >
      <div className="p-1">{children}</div>
    </div>
  );
}

export function SelectItem({ value, children }) {
  const { onValueChange } = React.useContext(SelectContext);
  const { setIsOpen } = React.useContext(SelectTriggerContext);

  return (
    <div
      className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100"
      onClick={() => {
        onValueChange(value);
        setIsOpen(false);
      }}
    >
      {children}
    </div>
  );
}
